import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui';
import { AppLogo } from '@/components/icons/AppLogo';
import { useOpenLogin, useOpenSignup } from '@/store/authModalStore';
import { useSignupTrack } from '@/analytics';

const TEST_ID_PREFIX = 'guest-top-bar';

export interface GuestTopBarProps {
  /** Additional CSS classes applied to the root element. */
  className?: string;
}

/**
 * Top navigation bar for unauthenticated users.
 *
 * Shows the Crown logo on the left with Login and Sign Up buttons
 * on the right. Triggers the auth modal on click.
 */
export function GuestTopBar({ className }: GuestTopBarProps) {
  const openLogin = useOpenLogin();
  const openSignup = useOpenSignup();
  const track = useSignupTrack();

  // The Lander header is the funnel's primary fresh-start entry: it opens the
  // Methods sheet for a visitor with no session. `source` is threaded into the
  // store so the eventual `sign_up_completed` reports the same entry point.
  function startSignup() {
    track('sign_up_started', { source: 'lander_header' });
    openSignup('lander_header');
  }

  return (
    <div data-testid={TEST_ID_PREFIX} className={cn('flex items-center justify-between px-2 h-[49px] gap-2', className)}>
      <Link to="/" aria-label="Go to home" className="inline-block w-fit">
        <AppLogo width={170} disableAnimation={true} />
      </Link>

      <div className="flex items-center gap-2">
        <Button
          data-testid={`${TEST_ID_PREFIX}-login-button`}
          variant="outline"
          size="sm"
          onClick={openLogin}
          className="h-[39px] w-[70px] border-purple text-purple"
        >
          Log In
        </Button>
        <Button
          data-testid={`${TEST_ID_PREFIX}-signup-button`}
          size="sm"
          onClick={startSignup}
          className="h-[39px] w-[79px] bg-none bg-purple border-[1.5px] border-purple rounded-pill text-white shadow-none [box-shadow:inset_0px_2px_4px_rgba(255,255,255,0.25)]"
        >
          Sign Up
        </Button>
      </div>
    </div>
  );
}
